import { z } from 'zod';
import { TicketPriority } from '../enums.js';
/**
 * SLA targets per priority (SPEC.md §5.1.2).
 *
 * Values below are *defaults*, not constants: decision D8 makes the timeouts
 * ADMIN-configurable, so every function here takes a policy and only falls back to
 * DEFAULT_SLA_POLICY. Hardcoding them would contradict D8.
 */
const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
export const slaTargetSchema = z.object({
    /** Minutes from creation to acknowledgement. Also fires the OPEN → ESCALATED timer. */
    ackTimeoutMinutes: z.number().positive(),
    /** Hours from creation to resolution. */
    resolveTimeoutHours: z.number().positive(),
});
/**
 * All four priorities, required, and nothing else.
 *
 * Was a `z.record`, which validates each entry but not exhaustiveness — a stored
 * policy missing LOW would have parsed cleanly and then produced NaN deadlines at
 * the first LOW ticket. Strict, so a typo'd fifth key is a refusal at the PUT
 * rather than a silently ignored entry the operator believes is in force.
 */
export const slaPolicySchema = z
    .object({
    [TicketPriority.Urgent]: slaTargetSchema,
    [TicketPriority.High]: slaTargetSchema,
    [TicketPriority.Medium]: slaTargetSchema,
    [TicketPriority.Low]: slaTargetSchema,
})
    .strict();
export const DEFAULT_SLA_POLICY = {
    [TicketPriority.Urgent]: { ackTimeoutMinutes: 5, resolveTimeoutHours: 0.5 },
    [TicketPriority.High]: { ackTimeoutMinutes: 15, resolveTimeoutHours: 2 },
    [TicketPriority.Medium]: { ackTimeoutMinutes: 30, resolveTimeoutHours: 8 },
    [TicketPriority.Low]: { ackTimeoutMinutes: 60, resolveTimeoutHours: 24 },
};
/**
 * Stamped onto the ticket at creation (SPEC.md §5.1.2). Absolute instants, so a
 * client with a skewed clock or no network still renders the same deadline the
 * server enforces.
 */
export function computeSlaDeadlines(createdAt, priority, policy = DEFAULT_SLA_POLICY) {
    const target = policy[priority];
    const created = createdAt.getTime();
    return {
        ackDeadline: new Date(created + target.ackTimeoutMinutes * MINUTE_MS),
        resolveDeadline: new Date(created + target.resolveTimeoutHours * HOUR_MS),
    };
}
/**
 * Breached if acknowledgement came late, or has not come and the deadline has passed.
 * Passing `now` explicitly keeps this pure and testable.
 */
export function isAckBreached(ackDeadline, acknowledgedAt, now) {
    return (acknowledgedAt ?? now).getTime() > ackDeadline.getTime();
}
export function isResolveBreached(resolveDeadline, resolvedAt, now) {
    return (resolvedAt ?? now).getTime() > resolveDeadline.getTime();
}
/** Milliseconds left before acknowledgement breaches; negative once overdue. */
export function timeToAckDeadline(ackDeadline, now) {
    return ackDeadline.getTime() - now.getTime();
}
//# sourceMappingURL=sla-policy.js.map